import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, Lock, Loader2 } from 'lucide-react'
import { assets } from '../config/assets'
import { login } from '../api/auth'
import { useAuthStore } from '../store/authStore'

import UiButton from '../components/ui/UiButton'

function StatusMessage({ status }) {
  const { t } = useTranslation()

  const messages = {
    activated: t('login.statusActivated', 'Konto zostało aktywowane. Możesz się zalogować.'),
    'reset-sent': t('login.statusResetSent', 'Jeśli konto istnieje, wysłaliśmy link do resetowania hasła.'),
    'password-reset': t('login.statusPasswordReset', 'Hasło zostało zmienione. Zaloguj się.'),
  }

  if (!status || !messages[status]) return null

  return (
    <div className="mb-6 p-4 rounded-2xl bg-green-100/80 text-green-900 text-sm font-medium">
      {messages[status]}
    </div>
  )
}

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setUser = useAuthStore((state) => state.setUser)

  const returnUrl = searchParams.get('returnUrl') || '/moja-biblioteka'
  const status = searchParams.get('status')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const user = await login({ email, password })
      setUser(user)
      navigate(returnUrl)
    } catch (err) {
      setError(err.message || t('login.genericError', 'Wystąpił błąd. Spróbuj ponownie.'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-brand-blue px-4 py-12">
      <div
        className="absolute inset-0 bg-cover bg-fixed bg-center pointer-events-none"
        style={{ backgroundImage: `url(${assets.backgroundPattern})` }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          className="block text-center font-climate text-brand-gold text-3xl md:text-4xl mb-8 hover:text-brand-orange transition"
        >
          Abonamenty Literackie
        </Link>

        <div className="bg-white/25 backdrop-blur-md rounded-[2rem] border border-white/10 shadow-xl p-8 md:p-10">
          <h1 className="font-climate text-brand-blue text-3xl mb-2 text-center">
            {t('login.title', 'Zaloguj się')}
          </h1>
          <p className="text-brand-blue/80 text-center mb-8">
            {t('login.subtitle', 'Witaj w swojej bibliotece')}
          </p>

          <StatusMessage status={status} />

          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-red-100/80 text-red-900 text-sm font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-bold text-brand-blue mb-2">
                {t('login.email', 'E-mail')}
              </label>
              <div className="relative">
                <Mail
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  placeholder={t('login.emailPlaceholder', 'twoj@email.pl')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-brand-blue mb-2">
                {t('login.password', 'Hasło')}
              </label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-blue/50"
                  aria-hidden="true"
                />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full pl-11 pr-4 py-3 rounded-full bg-white/80 border border-white/20 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  placeholder={t('login.passwordPlaceholder', '••••••••')}
                />
              </div>
            </div>

            <UiButton
              type="submit"
              variant="gold"
              size="lg"
              fullWidth
              disabled={isLoading}
              icon={isLoading ? Loader2 : undefined}
            >
              {isLoading ? t('login.loading', 'Logowanie...') : t('login.submit', 'Zaloguj się')}
            </UiButton>
          </form>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <Link
              to="/forgot-password"
              className="text-brand-blue hover:text-brand-orange font-bold transition"
            >
              {t('login.forgotPassword', 'Zapomniałeś hasła?')}
            </Link>
            <Link
              to={`/register?returnUrl=${encodeURIComponent(returnUrl)}`}
              className="text-brand-blue hover:text-brand-orange font-bold transition"
            >
              {t('login.register', 'Załóż konto')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
